import React, { useState } from 'react';
import {
    Box, Typography, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, TextField, DialogActions,
    FormControl, InputLabel, Select, MenuItem, useMediaQuery,
} from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import baseURL from '../../../user/config';
import ConfirmationDialog from '../../ConfirmationDialog';

const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
};

// This component now receives `members` and `refetchMembers` as props.
const MemberManagement = ({ members, refetchMembers }) => {
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState({ id: null, type: '' });
    const [newUser, setNewUser] = useState({ name: '', email: '', role: 'Viewer' });
    const isSmallScreen = useMediaQuery("(max-width:600px)");

    // --- Handlers for backend interactions ---
    // These functions now call `refetchMembers()` after a successful API call.
    const handleToggleActive = async (id, currentStatus) => {
        try {
            const response = await fetch(`${baseURL}/org/members/${id}/status`, {
                method: 'PUT',
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to update user status.');
            }
            toast.success(data.message);
            refetchMembers(); // Trigger a data refresh
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDeleteUser = async (id) => {
        try {
            const response = await fetch(`${baseURL}/org/members/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete user.');
            }
            toast.success(data.message);
            refetchMembers(); // Trigger a data refresh
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleAddUser = async () => {
        if (!newUser.name || !newUser.email) {
            toast.error("Please fill in all fields.");
            return;
        }
        try {
            const response = await fetch(`${baseURL}/org/members`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(newUser),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to add member.');
            }
            toast.success(data.message);
            handleCloseAddDialog();
            refetchMembers(); // Trigger a data refresh
        } catch (error) {
            toast.error(error.message);
        }
    };

    // --- (The rest of the component logic for dialogs and UI) ---
    const handleOpenConfirmation = (id, type) => {
        setPendingAction({ id, type });
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmation = () => {
        setOpenConfirmDialog(false);
        setPendingAction({ id: null, type: '' });
    };

    const handleConfirmAction = () => {
        const { id, type } = pendingAction;
        handleCloseConfirmation();
        if (type === 'delete') {
            handleDeleteUser(id);
        } else if (type === 'toggleActive') {
            const userToUpdate = members.find(u => u.id === id);
            if (userToUpdate) {
                handleToggleActive(id, userToUpdate.active);
            }
        }
    };

    const handleOpenAddDialog = () => setOpenAddDialog(true);
    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setNewUser({ name: '', email: '', role: 'Viewer' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prev => ({ ...prev, [name]: value }));
    };

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: MS_COLORS.background,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                mt: 3,
                border: `1px solid ${MS_COLORS.border}`,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    color: MS_COLORS.text,
                    mb: 3,
                    fontWeight: "bold",
                    fontSize: isSmallScreen ? "1.1rem" : "1.4rem",
                }}
            >
                Member Management
            </Typography>

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 1,
                    boxShadow: 0,
                    overflowX: "auto",
                    border: `1px solid ${MS_COLORS.border}`,
                    mb: 3
                }}
            >
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: MS_COLORS.text }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: MS_COLORS.text }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: MS_COLORS.text }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: MS_COLORS.text }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: MS_COLORS.text }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {members.map((user) => (
                            <TableRow
                                key={user.id}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    '&:hover': { backgroundColor: MS_COLORS.hoverBackground }
                                }}
                            >
                                <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{user.name}</TableCell>
                                <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{user.email}</TableCell>
                                <TableCell sx={{ fontSize: "0.8rem", fontWeight: 'bold', color: MS_COLORS.text }}>{user.role}</TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: user.active ? MS_COLORS.accent : MS_COLORS.red,
                                            fontWeight: 'bold',
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {user.active ? 'Active' : 'Inactive'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleOpenConfirmation(user.id, 'toggleActive')}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            borderRadius: 1,
                                            borderColor: user.active ? MS_COLORS.red : MS_COLORS.accent,
                                            color: user.active ? MS_COLORS.red : MS_COLORS.accent,
                                            mr: 1,
                                            '&:hover': {
                                                borderColor: user.active ? MS_COLORS.red : MS_COLORS.accent,
                                                backgroundColor: user.active ? MS_COLORS.red + '10' : MS_COLORS.accent + '10',
                                            }
                                        }}
                                    >
                                        {user.active ? "Inactivate" : "Activate"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleOpenConfirmation(user.id, 'delete')}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            borderRadius: 1,
                                            borderColor: MS_COLORS.red,
                                            color: MS_COLORS.red,
                                            '&:hover': {
                                                borderColor: MS_COLORS.red,
                                                backgroundColor: MS_COLORS.red + '10',
                                            }
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Button
                    variant="contained"
                    onClick={handleOpenAddDialog}
                    sx={{
                        backgroundColor: MS_COLORS.primary,
                        color: MS_COLORS.white,
                        textTransform: 'none',
                        fontSize: "0.85rem",
                        py: 1,
                        px: 2,
                        borderRadius: 1,
                        boxShadow: 0,
                        '&:hover': {
                            backgroundColor: MS_COLORS.primary,
                            opacity: 0.9,
                            boxShadow: 0,
                        },
                    }}
                    startIcon={<FaPlus />}
                >
                    Add New Member
                </Button>
            </Box>

            {/* Dialog to add a new member */}
            <Dialog open={openAddDialog} onClose={handleCloseAddDialog} PaperProps={{ sx: { borderRadius: "8px", p: 0, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}>
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
                    Add New Member
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="name"
                        label="Full Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newUser.name}
                        onChange={handleInputChange}
                        sx={{ mt: 2, mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                    />
                    <TextField
                        margin="dense"
                        name="email"
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={newUser.email}
                        onChange={handleInputChange}
                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                    />
                    <FormControl fullWidth sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}>
                        <InputLabel id="role-label">Role</InputLabel>
                        <Select
                            labelId="role-label"
                            id="role-select"
                            name="role"
                            value={newUser.role}
                            label="Role"
                            onChange={handleInputChange}
                        >
                            <MenuItem value="Admin">Admin</MenuItem>
                            <MenuItem value="User">User</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseAddDialog} sx={{ color: MS_COLORS.text, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        onClick={handleAddUser}
                        variant="contained"
                        sx={{
                            backgroundColor: MS_COLORS.primary,
                            color: MS_COLORS.white,
                            textTransform: 'none',
                            boxShadow: 0,
                            '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
                        }}
                    >
                        Add Member
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reusable Confirmation Dialog */}
            <ConfirmationDialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmation}
                onConfirm={handleConfirmAction}
                title={pendingAction.type === 'delete' ? 'Confirm Deletion' : 'Confirm Status Change'}
                message={pendingAction.type === 'delete'
                    ? 'Are you sure you want to delete this member? This action cannot be undone.'
                    : 'Are you sure you want to change the status of this member?'}
                confirmText={pendingAction.type === 'delete' ? 'Delete' : 'Confirm'}
                cancelText="Cancel"
            />
        </Box>
    );
};

export default MemberManagement;